import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { UpdateNotice } from "./ui/update-notice";

import { AuthProvider } from "./auth/auth-context";
import { CalendarPage } from "./calendar/calendar-page";
import { HomePage } from "./home/home-page";
import { AcceptInvitePage } from "./routes/accept-invite-page";
import { ForgotPasswordPage } from "./routes/forgot-password-page";
import { NotFoundPage } from "./routes/not-found-page";
import { ProductsPage } from "./routes/products-page";
import { ResetPasswordPage } from "./routes/reset-password-page";
import { EnterPage } from "./routes/enter-page";
import { ChangePasswordPage } from "./routes/change-password-page";
import { OperatorPage } from "./operator/operator-page";
import { LoginPage } from "./routes/login-page";
import { SignupPage } from "./signup/signup-page";
import { WelcomePage } from "./signup/welcome-page";
import { PatientsPage } from "./patients/patients-page";
import { InventoryPage } from "./inventory/inventory-page";
import { AccountingPage } from "./accounting/accounting-page";
import { ModuleGate, RequireModule } from "./shell/locked-module";
import { SupplyPage } from "./supply/supply-page";
import { PlanHistoryPage } from "./plans/plan-history-page";
import { ProceduresPage } from "./plans/procedures-page";
import { RequirePermission } from "./routes/require-admin";
import { SYSTEM_PERMISSIONS } from "./auth/use-can";
import { RequireAuth } from "./routes/require-auth";
import { BranchPage } from "./settings/branch-page";
import { GooglePane, LocationsPane, PeoplePane, PracticePane } from "./settings/clinic-panes";
import { BlockReasonsPane, ChairsPane, HoursPane } from "./settings/location-panes";
import { PersonPage } from "./settings/person-page";
import { CalendarViewPane } from "./settings/calendar-view-pane";
import { HrWarningsPane } from "./settings/hr-warnings-pane";
import { ModulesPane } from "./settings/modules-pane";
import { RemindersPane } from "./settings/reminders-pane";
import { SettingsIndex, SettingsLayout, StandalonePane } from "./settings/settings-layout";
import { AccessPane } from "./settings/access-pane";
import { StaffPage } from "./staff/staff-page";
import { HrIndex, PersonRedirect } from "./routes/structure-redirects";
import { SetupPage } from "./setup/setup-page";
import { AppShell } from "./shell/app-shell";
import { ThemeProvider } from "./theme/theme-context";
import { TasksPage } from "./tasks/tasks-page";
import { WorkflowEditorPage } from "./workflows/workflow-editor";
import { WorkflowsPage } from "./workflows/workflows-page";

/**
 * The route table.
 *
 * Everything except `/login` sits behind `RequireAuth` and inside `AppShell`,
 * so a new screen is protected and framed by where it is added rather than by
 * remembering to do either — the same direction the API's guards fail in.
 *
 * The five destinations are the mockup's five, in its order. **No screen is the
 * "main" one** (CLAUDE.md): Home is the index because that is where the mockup
 * starts, not because it outranks the others.
 */
export function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            {/*
              The suite's front door (2026-09-06). Public, and the first thing a
              signed-out visitor meets — `RequireAuth` sends them here rather
              than straight to the sign-in form, because which product you want
              is a question that comes before who you are.
            */}
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Public, and outside the shell: the person holding the link has
                no account yet, so there is nothing to draw a sidebar for. */}
            <Route path="/accept/:token" element={<AcceptInvitePage />} />

            {/*
              Password reset (STORY-063b), **five days late**.

              The API has mailed links to `/reset/:token` since 2026-08-31 and
              nothing here matched them, so a reset email opened a blank page.
              `/forgot` is the other half — there was no way to *ask* for a link
              either, so the feature was unreachable from both ends.
            */}
            <Route path="/forgot" element={<ForgotPasswordPage />} />
            <Route path="/reset/:token" element={<ResetPasswordPage />} />

            {/*
              Arriving from the apex clinic chooser (2026-09-20).

              **Public, and it only ever runs at a clinic’s own origin** — the
              same shape as /welcome/:token and for the same reason: the session
              has to be stored where the practice lives, and localStorage does not
              cross origins. The password was verified at the apex before the
              practices were named, so this screen asks for nothing.
            */}
            <Route path="/enter/:token" element={<EnterPage />} />

            {/*
              Self-serve sign-up (2026-09-05), and the landing that follows it.

              **Both public, and `/signup` is the only screen served at the
              apex** — a practice choosing its subdomain does not have one yet.
              `/welcome/:token` is the opposite: it only ever runs at the new
              clinic's own origin, because that is where the session has to be
              stored. See `signup-handoff.ts` for why the two cannot be one page.
            */}
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/welcome/:token" element={<WelcomePage />} />

            {/*
              The operator console (STORY-063, decisions O1–O2).

              **Outside `RequireAuth` and outside `AppShell`, and neither is an
              oversight.** `RequireAuth` guards a *clinic* session, which an
              operator does not have and never will; the shell draws a sidebar,
              a branch switcher and a clinic's logo, none of which mean anything
              here. The page renders its own sign-in and holds its own session.

              **It is not protected by being unlisted.** Nothing here is: every
              `/operator` endpoint refuses a request without an operator token,
              and a clinic user's token cannot become one — they are signed with
              different secrets. A clinic user who types this address gets a
              sign-in form they cannot pass, which is the same thing a stranger
              gets. What the route being hidden buys is only that no clinic user
              is ever shown a door that is not theirs.
            */}
            <Route path="/operator" element={<OperatorPage />} />

            <Route element={<RequireAuth />}>
              {/*
                **Inside `RequireAuth` and outside `AppShell`.** It needs a
                session — you cannot change a password without being signed in —
                and it must not be framed by a sidebar leading to screens the
                API is refusing.
              */}
              <Route path="/change-password" element={<ChangePasswordPage />} />

              <Route element={<AppShell />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                {/* A preview like Tasks (WG-D15): each page says so when it is off. */}
                <Route path="/workflows" element={<WorkflowsPage />} />
                <Route path="/workflows/new" element={<WorkflowEditorPage />} />
                <Route path="/workflows/:workflowId" element={<WorkflowEditorPage />} />
                {/*
                  Two paths, one screen. The patient id lives in the URL rather
                  than in component state, so a file can be linked to and a
                  reload does not lose the clinician's place.
                */}
                <Route path="/patients" element={<PatientsPage />} />
                <Route path="/patients/:patientId" element={<PatientsPage />} />
                {/*
                  **"Treatment plans" opens the procedure list** (PV7): the
                  procedures, their prices and the visits each takes. Patient
                  plans are no longer built (PV6). Not behind RequireAdmin —
                  every role reads it, and only an admin is drawn the controls
                  that change it.
                */}
                <Route path="/plans" element={<ProceduresPage />} />
                {/*
                  **Old addresses, kept as redirects** so a bookmark lands on
                  the list rather than on `/plans/:planId` reading the word as
                  a plan id: templates (DT1), the procedures page's own address
                  before it became the destination (PV7), and the worklist that
                  moved to Home (TP33b).
                */}
                <Route path="/plans/templates" element={<Navigate to="/plans" replace />} />
                <Route path="/plans/templates/*" element={<Navigate to="/plans" replace />} />
                <Route path="/plans/procedures" element={<Navigate to="/plans" replace />} />
                <Route path="/plans/waiting" element={<Navigate to="/" replace />} />
                {/* One course or old plan, read-only — links still point here (PV6). */}
                <Route path="/plans/:planId" element={<PlanHistoryPage />} />
                {/*
                  Stock, which used to be the last section of a location's
                  settings page. **Deliberately not behind RequireAdmin**, and
                  that is the whole point of the move: TP17 widened adjusting a
                  count and marking a unit opened past admins — "the person who
                  opens a tube is the person at the chair" — and `GET /inventory`
                  serves every signed-in user accordingly. Behind the admin gate
                  it was unreachable by exactly the people the decision named.
                  Creating, editing and retiring stay `@Roles("ADMIN")` on the
                  server, and the panel offers those buttons only to an admin.
                */}
                {/*
                  All stock is Supply (MS-D2): locked, with the prompt, without it —
                  and, like every other Supply page, only for someone who may see
                  stock, rather than an empty shelf under a refusal.
                */}
                <Route element={<RequirePermission anyOf={["inventory.view"]} />}>
                  <Route
                    path="/inventory"
                    element={
                      <ModuleGate module="SUPPLY">
                        <InventoryPage />
                      </ModuleGate>
                    }
                  />
                </Route>
                {/*
                  Supply's pages (MS-D4, MS-D8): Products → Catalogue and Supplier
                  items, Orders → Cart and Order history, People → Suppliers.
                  Keyed, so moving between them starts each page fresh.
                */}
                <Route element={<RequirePermission anyOf={["inventory.view"]} />}>
                  {(
                    [
                      ["/supply/catalogue", "catalogue"],
                      ["/supply/items", "items"],
                      ["/orders/cart", "cart"],
                      ["/orders/history", "history"],
                      ["/people/suppliers", "suppliers"],
                    ] as const
                  ).map(([path, section]) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <ModuleGate module="SUPPLY">
                          <SupplyPage key={section} section={section} />
                        </ModuleGate>
                      }
                    />
                  ))}
                  {/* The old single Supply page. */}
                  <Route path="/supply" element={<Navigate to="/supply/catalogue" replace />} />
                </Route>
                {/*
                  People → Staff (MS-D3): who works here, moved out of System.
                  The same pane, with its location scope, outside System's rail.
                */}
                <Route element={<RequirePermission anyOf={["people.manage", "access.assign"]} />}>
                  <Route element={<StandalonePane testId="people-staff-page" />}>
                    <Route path="/people/staff" element={<PeoplePane />} />
                    <Route path="/people/staff/:userId" element={<PersonPage />} />
                  </Route>
                </Route>
                {/*
                  The rota (Phase 9). **Deliberately not behind RequireAdmin**:
                  STORY-067 records that this destination is read by every role,
                  unlike Settings — a dentist needs to see their own week
                  without an admin opening it for them.
                */}
                {/*
                  HR's four pages (MS-D3). `/staff` was the one page with tabs;
                  it lands on the first HR page this person has.
                */}
                <Route path="/staff" element={<HrIndex />} />
                <Route path="/hr" element={<HrIndex />} />
                <Route path="/hr/shifts" element={<StaffPage key="shifts" page="shifts" />} />
                <Route path="/hr/requests" element={<StaffPage key="requests" page="requests" />} />
                <Route path="/hr/staff" element={<StaffPage key="staff" page="staff" />} />
                <Route path="/hr/me" element={<StaffPage key="me" page="me" />} />
                {/*
                  Accounting (STORY-172). Behind its own permission rather than
                  `billing.reports.view`, which Practice manager also holds:
                  this screen shows everyone's salary, and AC-D13 gives that to
                  Admin and Finance officer only. The API refuses on its own;
                  this only keeps the screen from opening onto refusals.
                */}
                <Route
                  element={
                    <RequirePermission
                      anyOf={[
                        "accounting.view",
                        "accounting.expenses.view",
                        "accounting.salaries.view",
                      ]}
                    />
                  }
                >
                  <Route
                    path="/accounting"
                    element={
                      <ModuleGate module="ACCOUNTING">
                        <AccountingPage />
                      </ModuleGate>
                    }
                  />
                </Route>
                {/*
                  The sixth destination, behind its own gate (decisions S2, S3).
                  A non-admin is bounced home rather than shown controls the
                  server would refuse — the sidebar hides the item for the same
                  reason, and neither is the permission check.
                */}
                <Route element={<RequirePermission anyOf={SYSTEM_PERMISSIONS} />}>
                  {/*
                    Guided setup (STORY-069). An ordinary route, deliberately:
                    signing in is what sends a new clinic here (decision G3),
                    and after that it is a page you can leave and come back to
                    — which a gate bouncing every route until setup finished
                    would have made impossible.
                  */}
                  <Route path="/setup" element={<SetupPage />} />
                  {/*
                    **Settings is a rail and a pane, and every destination has
                    its own URL.**

                    It used to be two levels mirroring the data — a clinic has
                    locations, a location has chairs — with one route per level.
                    That put five of the nine things an admin configures behind
                    "open a location and scroll", named nowhere, which is the
                    complaint this restructure answers: an admin looking for a
                    price should not have to know that prices belong to a
                    branch.

                    So the nine are nine routes under one layout, and the four
                    that belong to a location take it from the rail's switcher
                    rather than from the path. `/settings` itself redirects
                    rather than rendering, because a layout with no pane is a
                    blank half-screen — `replace`, so Back does not bounce off
                    it.

                    `locations/:branchId` survives, still carrying its id: a
                    location's own details are per-location, and a link
                    somebody kept still has to work.
                  */}
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route index element={<SettingsIndex />} />
                    <Route path="practice" element={<PracticePane />} />
                    <Route path="locations" element={<LocationsPane />} />
                    <Route path="locations/:branchId" element={<BranchPage />} />
                    <Route path="hr-warnings" element={<HrWarningsPane />} />
                    <Route path="modules" element={<ModulesPane />} />
                    <Route path="hours" element={<HoursPane />} />
                    <Route path="chairs" element={<ChairsPane />} />
                    {/*
                      Moved to `/plans/procedures` (PL1). Kept as a redirect so
                      a bookmark or an old link lands on the list, not on 404.
                    */}
                    <Route path="procedures" element={<Navigate to="/plans" replace />} />
                    <Route path="block-reasons" element={<BlockReasonsPane />} />
                    {/* Chatbot's, so hidden outright without it (QF-D9, QF-D22). */}
                    <Route
                      path="reminders"
                      element={
                        <RequireModule module="CHATBOT" to="/settings">
                          <RemindersPane />
                        </RequireModule>
                      }
                    />
                    <Route path="calendar-view" element={<CalendarViewPane />} />
                    {/* Moved to People → Staff (MS-D3); kept so old links land. */}
                    <Route path="people" element={<Navigate to="/people/staff" replace />} />
                    <Route path="people/:userId" element={<PersonRedirect />} />
                    <Route path="access" element={<AccessPane />} />
                    <Route path="google" element={<GooglePane />} />
                  </Route>
                </Route>
              </Route>
            </Route>

            {/*
              **Last, and it must stay last.** Without it React Router renders
              nothing at all for an unmatched path — a white page with no text
              and no console error, which is what every mistyped URL and every
              stale emailed link produced until now.
            */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          {/* On every screen, signed in or not: a tab opened before a deploy
              offers to load the new version (2026-10-05). */}
          <UpdateNotice />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
